'use client';
import { jsx } from '@emotion/react/jsx-runtime';
import { forwardRef } from 'react';
import { createPolymorphicComponent, OptionalPortal, LockScroll } from '@s7/base';
import { useProps } from '@s7/styles';
import { ModalProvider } from './Modal.context.js';
import { IDENT_MODAL, defaultProps } from './Modal.contract.js';
import { useModal } from './useModal.js';
import { useShouldLockScroll } from './useShouldLockScroll.js';
import useStyles from './Modal.styles.js';
import { ModalBackdrop } from './ModalBackdrop/ModalBackdrop.js';
import { ModalContent } from './ModalContent/ModalContent.js';
import { ModalTitle } from './ModalContent/ModalHeader/ModalTitle/ModalTitle.js';
import { ModalBody } from './ModalContent/ModalBody/ModalBody.js';
import { ModalHeader } from './ModalContent/ModalHeader/ModalHeader.js';

function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const _Modal = /*#__PURE__*/ forwardRef((_param, ref)=>{
    var { __ident = IDENT_MODAL } = _param, props = _object_without_properties_loose(_param, [
        "__ident"
    ]);
    const _useProps = useProps(__ident, props, defaultProps), [{ className, keepMounted, opened, onClose, onKeyDown, id, transitionProps, trapFocus, closeOnEscape, returnFocus, closeOnClickOutside, withinPortal, portalProps, lockScroll, children, zIndex, padding, radius, breakpoints, getLockScrollProps }, sxs] = _useProps, others = _object_without_properties_loose(_useProps[0], [
        "className",
        "keepMounted",
        "opened",
        "onClose",
        "onKeyDown",
        "id",
        "transitionProps",
        "trapFocus",
        "closeOnEscape",
        "returnFocus",
        "closeOnClickOutside",
        "withinPortal",
        "portalProps",
        "lockScroll",
        "children",
        "zIndex",
        "padding",
        "radius",
        "breakpoints",
        "getLockScrollProps"
    ]);
    const [classes, cx] = useStyles(null, sxs, __ident);
    const { _id, titleMounted, bodyMounted, setTitleMounted, setBodyMounted, onModalKeydown } = useModal({
        id,
        opened,
        trapFocus,
        closeOnEscape,
        onClose,
        returnFocus
    });
    var _transitionProps_duration;
    const shouldLockScroll = useShouldLockScroll({
        transitionDuration: (_transitionProps_duration = transitionProps == null ? void 0 : transitionProps.duration) != null ? _transitionProps_duration : 200,
        lockScroll,
        opened
    });
    const getTitleId = ()=>`${_id}-title`;
    const getBodyId = ()=>`${_id}-body`;
    return /*#__PURE__*/ jsx(OptionalPortal, _extends({}, portalProps, {
        withinPortal: withinPortal,
        children: /*#__PURE__*/ jsx(ModalProvider, {
            value: {
                opened,
                onClose,
                onKeyDown,
                closeOnClickOutside,
                transitionProps: _extends({}, transitionProps, {
                    keepMounted
                }),
                getTitleId,
                getBodyId,
                titleMounted,
                bodyMounted,
                setTitleMounted,
                setBodyMounted,
                trapFocus,
                closeOnEscape,
                zIndex,
                padding,
                radius,
                breakpoints,
                preset: sxs.preset
            },
            children: /*#__PURE__*/ jsx(LockScroll, _extends({
                enabled: shouldLockScroll
            }, getLockScrollProps == null ? void 0 : getLockScrollProps(shouldLockScroll), {
                ref: ref,
                className: cx(classes.root, className),
                onKeyDown: onModalKeydown
            }, others, {
                children: children
            }))
        })
    }));
});
const Modal = createPolymorphicComponent(_Modal);
Modal.displayName = IDENT_MODAL;
Modal.Backdrop = ModalBackdrop;
Modal.Content = ModalContent;
Modal.Title = ModalTitle;
Modal.Body = ModalBody;
Modal.Header = ModalHeader;
var Modal$1 = Modal;

export { Modal, Modal$1 as default };
//# sourceMappingURL=Modal.js.map
